import React, {
  useState, useRef, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle,
} from 'react';

export interface SmartVariable {
  name: string;
  label: string;
  description: string;
  group: string;
}

interface SmartHtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  variables: SmartVariable[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  spellCheck?: boolean;
}

export interface SmartHtmlEditorHandle {
  insertText: (text: string) => void;
  focus: () => void;
}

/* ── Syntax highlighter (lightweight regex-based) ── */
function highlightHtml(code: string): string {
  // First, escape HTML so the pre>code display is safe
  let html = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // 1. HTML comments
  html = html.replace(
    /(&lt;!--[\s\S]*?--&gt;)/g,
    '<span style="color:#94a3b8;font-style:italic">$1</span>'
  );

  // 2. Handlebars {{{raw}}} / {{expr}}
  html = html.replace(
    /(\{\{\{?)([^}]+)(\}\}\}?)/g,
    '<span style="color:#9333ea;font-weight:600">$1$2$3</span>'
  );

  // 3. Strings (inside value-context) — after HB so we don't re-color inside spans
  html = html.replace(
    /(&quot;.*?&quot;)/g,
    '<span style="color:#059669">$1</span>'
  );

  // 4. HTML tags (escaped as &lt;tag&gt;)
  html = html.replace(
    /(&lt;\/?)([a-zA-Z][a-zA-Z0-9]*)([^&]*?)(\/?&gt;)/g,
    (match, open, tagName, attrs, close) => {
      return (
        `<span style="color:#2563eb">${open}${tagName}</span>` +
        `<span style="color:#475569">${attrs}</span>` +
        `<span style="color:#2563eb">${close}</span>`
      );
    }
  );

  // Ensure trailing newline so pre has same height on last empty line
  if (!html.endsWith('\n') && !html.endsWith('<br>')) html += '\n';
  return html;
}

/* ── Utility: compute caret pixel coordinates inside a textarea ── */
function getCaretPixelPosition(
  textarea: HTMLTextAreaElement,
  position: number
): { left: number; top: number } {
  const style = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  mirror.style.cssText = `
    position:fixed; top:0; left:0; visibility:hidden;
    white-space:pre-wrap; word-wrap:break-word; overflow-wrap:break-word;
    width:${textarea.clientWidth}px;
    padding:${style.padding};
    border:${style.border};
    font:${style.font};
    line-height:${style.lineHeight};
    letter-spacing:${style.letterSpacing};
    text-indent:${style.textIndent};
    box-sizing:border-box;
  `;
  const text = textarea.value.substring(0, position).replace(/\n$/, '\n ');
  const textNode = document.createTextNode(text);
  const span = document.createElement('span');
  span.textContent = '\u200b'; // zero-width space marker
  mirror.appendChild(textNode);
  mirror.appendChild(span);
  document.body.appendChild(mirror);

  const spanRect = span.getBoundingClientRect();
  const taRect = textarea.getBoundingClientRect();

  document.body.removeChild(mirror);

  return {
    left: spanRect.left - taRect.left + textarea.scrollLeft,
    top: spanRect.top - taRect.top + textarea.scrollTop + parseFloat(style.lineHeight || '16'),
  };
}

const SmartHtmlEditor = forwardRef<SmartHtmlEditorHandle, SmartHtmlEditorProps>(
  function SmartHtmlEditor({ value, onChange, variables, placeholder, className, disabled, spellCheck = false }, ref) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const preRef = useRef<HTMLPreElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Autocomplete state
    const [acOpen, setAcOpen] = useState(false);
    const [acQuery, setAcQuery] = useState('');
    const [acIdx, setAcIdx] = useState(0);
    const [acPos, setAcPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
    const acStartRef = useRef<number>(-1);
    const ignoreBlurRef = useRef(false);

    const lineNumbers = useMemo(() => {
      const count = value.split('\n').length;
      return Array.from({ length: count }, (_, i) => i + 1).join('\n');
    }, [value]);

    const highlighted = useMemo(() => highlightHtml(value), [value]);

    /* ── Scroll sync: sync pre layer and line numbers to textarea scroll ── */
    useEffect(() => {
      const ta = textareaRef.current;
      const pre = preRef.current;
      if (!ta || !pre) return;
      let rafId: number | null = null;
      const sync = () => {
        if (rafId !== null) return; // throttle to one rAF
        rafId = requestAnimationFrame(() => {
          pre.scrollTop = ta.scrollTop;
          pre.scrollLeft = ta.scrollLeft;
          rafId = null;
        });
      };
      ta.addEventListener('scroll', sync, { passive: true });
      return () => {
        ta.removeEventListener('scroll', sync);
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }, []);

    /* ── Variable candidates ── */
    const candidates = useMemo(() => {
      if (!acQuery) return variables;
      const q = acQuery.toLowerCase();
      return variables.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.label.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q)
      );
    }, [acQuery, variables]);

    const groupedCandidates = useMemo(() => {
      const groups: Record<string, SmartVariable[]> = {};
      candidates.forEach((v) => {
        groups[v.group] = groups[v.group] || [];
        groups[v.group].push(v);
      });
      return Object.entries(groups);
    }, [candidates]);

    /* ── Insert text at cursor programmatically ── */
    const insertText = useCallback(
      (text: string) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const newVal = value.slice(0, start) + text + value.slice(end);
        onChange(newVal);
        const newPos = start + text.length;
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(newPos, newPos);
        });
      },
      [value, onChange]
    );

    useImperativeHandle(ref, () => ({
      insertText,
      focus: () => textareaRef.current?.focus(),
    }));

    /* ── Autocomplete insertion ── */
    const commitVariable = useCallback(
      (varName: string) => {
        const ta = textareaRef.current;
        if (!ta || acStartRef.current < 0) return;
        const cursor = ta.selectionStart;
        const beforeAt = value.substring(0, acStartRef.current);
        const afterCursor = value.substring(cursor);
        const insert = `{{${varName}}}`;
        const newVal = beforeAt + insert + afterCursor;
        onChange(newVal);
        setAcOpen(false);
        setAcQuery('');
        acStartRef.current = -1;
        const newPos = beforeAt.length + insert.length;
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(newPos, newPos);
        });
      },
      [onChange, value]
    );

    /* ── Keyboard / typing handler ── */
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!acOpen) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setAcIdx((i) => Math.min(i + 1, candidates.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setAcIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const flat = candidates;
          if (flat[acIdx]) {
            commitVariable(flat[acIdx].name);
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setAcOpen(false);
          setAcQuery('');
          acStartRef.current = -1;
        }
      },
      [acOpen, candidates, acIdx, commitVariable]
    );

    /* ── Track @ or {{ in textarea ── */
    const handleInput = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
        const ta = e.target;
        const cursor = ta.selectionStart;

        let mentionStart = -1;
        let prefixLen = 1;

        // Check for {{ trigger first
        if (cursor >= 2 && ta.value.substring(cursor - 2, cursor) === '{{') {
          mentionStart = cursor;
          prefixLen = 2;
        } else {
          // Scan backwards for @ or {{
          for (let i = cursor - 1; i >= 0; i--) {
            const ch = ta.value[i];
            if (ch === '@' && (i === 0 || /[\s\n\t]/.test(ta.value[i - 1]))) {
              mentionStart = i + 1;
              prefixLen = 1;
              break;
            }
            if (i >= 1 && ta.value.substring(i - 1, i + 1) === '{{') {
              mentionStart = i + 1;
              prefixLen = 2;
              break;
            }
            if (ch === '\n' || ch === '}') break;
          }
        }

        if (mentionStart >= 0) {
          const rawStart = mentionStart - prefixLen;
          const query = ta.value.substring(mentionStart, cursor);
          acStartRef.current = rawStart;
          setAcQuery(query);
          setAcIdx(0);
          setAcOpen(true);
          const pos = getCaretPixelPosition(ta, cursor);
          setAcPos(pos);
        } else {
          setAcOpen(false);
          acStartRef.current = -1;
        }
      },
      [onChange]
    );

    /* ── Click outside to close autocomplete ── */
    useEffect(() => {
      if (!acOpen) return;
      const handler = (e: MouseEvent) => {
        const target = e.target as Node;
        if (containerRef.current && !containerRef.current.contains(target)) {
          setAcOpen(false);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [acOpen]);

    // Compute flat candidate for index-based selection across groups
    const flatCandidates = useMemo(() => candidates, [candidates]);

    // Styles shared by textarea and pre to ensure identical wrapping
    const sharedStyle: React.CSSProperties = {
      margin: 0,
      padding: '1rem',
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: '0.75rem',
      lineHeight: 1.625,
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      tabSize: 2,
    };

    /* ── Click editor area to focus textarea ── */
    const handleContainerClick = useCallback((e: React.MouseEvent) => {
      // Don't steal focus from autocomplete items
      if ((e.target as HTMLElement).closest('[data-ac-item]')) return;
      textareaRef.current?.focus();
    }, []);

    return (
      <div ref={containerRef} className={`relative flex flex-col min-h-0 ${className || ''}`} onClick={handleContainerClick}>
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Line numbers */}
          <div
            className="w-12 shrink-0 bg-slate-50 border-r border-slate-200 overflow-hidden pt-4 pr-2 text-right text-xs text-slate-500 font-mono select-none leading-relaxed"
            aria-hidden="true"
          >
            {lineNumbers.split('\n').map((n, i) => (
              <div key={i}>{n}</div>
            ))}
          </div>

          {/* Editor area */}
          <div className="relative flex-1 min-w-0 overflow-auto">
            {/* Syntax-highlighted layer (behind) */}
            <pre
              ref={preRef}
              className="absolute inset-0 m-0 p-0 pointer-events-none"
              style={sharedStyle}
              aria-hidden="true"
            >
              <code dangerouslySetInnerHTML={{ __html: highlighted + '\n ' }} />
            </pre>

            {/* Transparent textarea (interactive) */}
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              spellCheck={spellCheck}
              placeholder={placeholder}
              className="absolute inset-0 w-full h-full bg-transparent text-transparent caret-slate-800 resize-none border-0 focus:outline-none"
              style={sharedStyle}
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="off"
            />
          </div>
        </div>

        {/* ── @ Autocomplete menu ── */}
        {acOpen && (
          <div
            className="absolute z-[8500] w-80 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden flex flex-col"
            style={{
              top: `calc(1rem + ${acPos.top}px)`,
              left: `calc(1rem + 3rem + ${acPos.left}px)`,
              maxHeight: '24rem',
            }}
          >
            {/* Header / search hint */}
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                Insert merge field
              </span>
              <span className="text-[11px] text-slate-500">
                {candidates.length} match{candidates.length !== 1 ? 'es' : ''}
              </span>
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1 py-1">
              {groupedCandidates.length === 0 ? (
                <div className="px-4 py-4 text-center text-xs text-slate-500">
                  No fields match “{acQuery}”
                </div>
              ) : (
                groupedCandidates.map(([group, items]) => {
                  const groupStart = flatCandidates.findIndex((v) => v === items[0]);
                  return (
                    <div key={group}>
                      <div className="px-3 py-1 text-caption font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50">
                        {group}
                      </div>
                      {items.map((v, i) => {
                        const flatIndex = groupStart + i;
                        const active = flatIndex === acIdx;
                        return (
                          <button
                            key={v.name}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              commitVariable(v.name);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                              active
                                ? 'bg-brand-active'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <span className="font-semibold shrink-0 min-w-[7rem]">{v.label}</span>
                            <span className="text-[11px] text-slate-500 truncate">
                              {v.description}
                            </span>
                            <span className="ml-auto text-[10px] font-mono text-slate-300 shrink-0">
                              {'{{' + v.name + '}}'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer hints */}
            <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50/50 flex items-center gap-3 text-[11px] text-slate-500">
              <span><kbd className="px-1 bg-white border border-slate-200 rounded text-slate-500">↑</kbd></span>
              <span><kbd className="px-1 bg-white border border-slate-200 rounded text-slate-500">↓</kbd></span>
              <span>to navigate</span>
              <span className="mx-1">·</span>
              <span><kbd className="px-1 bg-white border border-slate-200 rounded text-slate-500">Enter</kbd></span>
              <span>to insert</span>
              <span className="mx-1">·</span>
              <span><kbd className="px-1 bg-white border border-slate-200 rounded text-slate-500">Esc</kbd></span>
              <span>to close</span>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default SmartHtmlEditor;
